import React from 'react';
import LegalPage from './LegalPage';

const H = ({ children }) => (
  <h2 className="text-[#242326] text-xl md:text-2xl font-medium font-['Inter'] mt-10 mb-3 first:mt-0">
    {children}
  </h2>
);

const Privacy = () => (
  <LegalPage title="Privacy Policy" lastUpdated="29 August 2022" testid="privacy-page">
    <H>Introduction</H>
    <p>
      At Grouth Marketing Limited (&ldquo;Grouth&rdquo;), we treat your personal information as private and
      confidential. We are dedicated to protecting your privacy and providing you with the highest level of
      security at any point of interaction with us. This Privacy Policy describes what personal information we
      collect, what we do with it and how we protect it.
    </p>
    <p>
      This Policy (together with our Terms and Conditions) sets out the basis on which any personal data we
      collect from you, or that you provide to us, will be processed by us. Please read the following carefully
      to understand our views and practices regarding your personal data and how we will treat it to ensure
      data protection and privacy of customers.
    </p>
    <p>
      By continuing to visit our website (www.grouth.co) and other Grouth customer touchpoints and by
      ticking/choosing the relevant boxes which seek your consent whenever they appear, you understand,
      accept and consent to the practices described in this Policy.
    </p>

    <H>Information we collect and use</H>
    <ul className="list-disc pl-6 space-y-2">
      <li>
        We collect information about you from a variety of sources, such as our website, applications,
        identification documents, and interactions with our care agents and other written or electronic
        communication reflecting information including your name, address, identification numbers, biometric
        information, telephone number, occupation, etc.
      </li>
      <li>
        We may also use your transactional history including your payment records, and other payment history
        and transactions records.
      </li>
      <li>
        We may also use information received from third parties such as family, solicitors, friends or
        employers, websites, social media pages made public by you, government agencies, regulators,
        supervisory or other agencies.
      </li>
      <li>
        We may also collect other information such as video footage of you whenever you walk into our office
        and telephone conversations when you call our contact centre.
      </li>
    </ul>
    <p>
      Note that we may collect information about your computer or mobile device, including where available
      your IP address, operating system and browser type, for system administration or for our own commercial
      purposes. This is statistical data about our users&rsquo; browsing actions and patterns, and does not
      identify any individual.
    </p>

    <H>Non-personal information collected by us</H>
    <p>
      In order to achieve our goal of providing you with the best marketing services, we sometimes collect
      certain information during your visits to perform certain tasks such as grant you access to some parts
      of our website or conduct research on your behaviour on our site in order to improve our services. We
      will not disclose your information to any person outside our organisation except as described in this
      Privacy Policy.
    </p>

    <H>Cookies</H>
    <p>
      Cookies are unique identifiers that we transfer to your device or computer to enable our systems to
      recognise your device or computer and to provide features to make your navigation experience unique
      and targeted. The acceptance of cookies is not a requirement for accessing our website. We only use
      cookies for your convenience in using the service and not for obtaining or using any other information
      about you. Your browser can be set to not accept cookies. However, if you do not accept cookies on your
      web browser or allow permissions on your device, your online service experience may be degraded.
    </p>
    <p>Examples of Cookies we use:</p>
    <ul className="list-disc pl-6 space-y-2">
      <li>
        <strong>Session Cookies</strong>: We use Session Cookies to operate our service. Session cookies
        expire at the end of your browser session and allow us to link your actions during that browser
        session.
      </li>
      <li>
        <strong>Preference Cookies</strong>: We use Preference Cookies to remember your preferences and
        actions, across multiple sites.
      </li>
      <li>
        <strong>Security Cookies</strong>: We use Security Cookies for security purposes.
      </li>
    </ul>

    <H>Use of personal information</H>
    <p>
      Any personal information provided by you to Grouth will be used with your consent, or when we have a
      lawful reason. These include:
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li>Cases where processing of personal data is required for the fulfilment of a contractual obligation.</li>
      <li>Cases where processing of personal data is required for compliance with legal and/or regulatory requirements.</li>
      <li>Cases where processing is required to protect your vital interest or that of any other natural person.</li>
      <li>Cases where processing is required for an activity to be carried out for significant public interest.</li>
      <li>Cases where processing is required for legitimate interests of Grouth or a third party insofar as this does not conflict with the requirements for the protection of your personal data.</li>
    </ul>
    <p>Your personal information will be used in:</p>
    <ul className="list-disc pl-6 space-y-2">
      <li>Updating and enhancing Grouth&rsquo;s records.</li>
      <li>Executing your instructions.</li>
      <li>Establishing your identity and assessing applications for our products and services.</li>
      <li>Pricing and designing our products and services.</li>
      <li>Administering our products and services.</li>
      <li>Managing our relationship with you.</li>
      <li>Identifying and investigating illegal activity such as fraud.</li>
      <li>Contacting you, for example to tell you about recent occurrences in the marketing sector or some event(s) of significance.</li>
      <li>Conducting and improving our businesses and improving your experience with us.</li>
      <li>Complying with our legal obligations and assisting government and law enforcement agencies or regulators.</li>
      <li>Identifying and informing you about other products or services that we think may be of interest to you.</li>
      <li>Delivering advertising to you directly or on our websites, providing updates on special deals and offers (unless you tell us otherwise).</li>
      <li>Recording conversations you have with us including phone calls, face-to-face meetings, letters, emails and any other kinds of communication. These recordings may be used to check your instructions and improve our service delivery.</li>
      <li>We may also collect, use and exchange your information in other ways permitted by law.</li>
    </ul>

    <H>Automated Processing</H>
    <p>
      We sometimes use automated systems and software to help us reach decisions about you, for example to
      process your data when you apply for some of our products and services. This type of processing is
      carried out under lawful basis, and you can contact us to request that automated processing be reviewed
      by a human being if you detect any inaccuracies in your personal data.
    </p>

    <H>Information we share</H>
    <p>
      Grouth, in efforts to provide you with excellent products and services may share your information in
      order to provide you with products or services you&rsquo;ve requested. We may also share your
      information where we have a public or legal duty to do so and when we have requested and received your
      permission to share it.
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li>
        <strong>With our affiliated entities</strong>: we may share your Personal Data with entities affiliated
        to us to, among other things, provide the Service you have requested or authorised.
      </li>
      <li>With other third parties for our business and other legal purposes and as permitted or required by law.</li>
      <li>
        We do not sell, trade, or otherwise transfer your personally identifiable information to unaffiliated
        third parties. This does not include our system support team and other parties who assist us in
        operating the website and providing the service to you.
      </li>
    </ul>

    <H>How we protect your information</H>
    <p>
      To protect your personal data we maintain technical, physical, and administrative security measures
      designed to provide reasonable protection for your Personal Data against loss, misuse, unauthorised
      access, disclosure, and alteration. The security measures include firewalls, data encryption, physical
      access controls to our data centres, and information access authorisation controls. Although we have
      taken measures to secure and keep your information confidential, no method of transmission over the
      Internet, or method of electronic storage can always guarantee 100% security.
    </p>

    <H>Where we store your information</H>
    <p>
      All Personal Information you provide to us is stored on our secure servers as well as secure physical
      locations and cloud infrastructure. Whenever your information is transferred to other locations, we
      will take all necessary steps to ensure that your data is handled securely and in accordance with this
      Privacy Policy.
    </p>

    <H>How long we store your information</H>
    <p>
      We will keep your personal data for as long as we have a relationship with you, i.e., for as long as
      you use the service, or partake in surveys. Once that relationship has ended, we will only retain
      personal data for as long as it is required for the purpose for which you have given it and as required
      by law. Data which is not retained is securely destroyed when it is identified that it is no longer
      needed.
    </p>

    <H>Your rights</H>
    <ul className="list-disc pl-6 space-y-2">
      <li>The right to access your personal information held by us.</li>
      <li>The right to rectify inaccurate or incomplete information.</li>
      <li>The right to withdraw consent for processing where consent has previously been given.</li>
      <li>The right to restrict or object to processing of your personal data.</li>
      <li>The right to request that your personal data be made available to you in a common electronic format.</li>
      <li>The right to request that your information be erased.</li>
    </ul>
    <p>
      Your right of access can be exercised by sending an email to{' '}
      <a href="mailto:ademola@grouth.co" className="text-[#83BF42] underline">ademola@grouth.co</a>.
    </p>

    <H>Third party sites and services</H>
    <p>
      Grouth&rsquo;s websites, products, applications, and services may contain links to third-party
      websites. Information collected by third parties is governed by their privacy practices and Grouth will
      not be liable for any breach of confidentiality or privacy of your information on such sites.
    </p>

    <H>Privacy of Minors</H>
    <p>
      The services on the website are not directed to anyone under the age of 18. We do not knowingly
      collect names, email addresses, or any other personal data from minors. If we obtain actual knowledge
      that we have collected Personal Data from a person under the age of 18, we reserve the right to delete
      it, unless we are legally obligated to retain such data.
    </p>

    <H>Promotional messages</H>
    <p>
      Grouth may sometimes contact you with products or services that we think may be of interest to you. If
      you don&rsquo;t want to receive such promotional materials, you can opt out at any time by using any
      unsubscribe links at the bottom of our emails or by sending an email to{' '}
      <a href="mailto:ademola@grouth.co" className="text-[#83BF42] underline">ademola@grouth.co</a>.
    </p>

    <H>Complaints Resolution</H>
    <p>
      Where you have any complaints about your privacy, kindly submit such complaint to us first by sending
      an email to{' '}
      <a href="mailto:ademola@grouth.co" className="text-[#83BF42] underline">ademola@grouth.co</a> or
      addressing your complaint to &ldquo;The Data Protection Officer&rdquo; at our address. If you are not
      satisfied with our response, you can contact any regulatory body you deem fit to resolve your request.
    </p>

    <H>Privacy Policy changes</H>
    <p>
      We may revise this Privacy Policy from time to time to reflect changes to our business, website or
      service, or applicable laws. The revised Privacy Policy will be effective as of the published effective
      date. Your continued use of the website after we post any modifications to the Privacy Policy will
      constitute your acknowledgment of the modifications and your consent to abide and be bound by the
      modified Privacy Policy.
    </p>

    <H>Contact</H>
    <p>
      Questions, comments and further information on anything included in this Privacy Policy are welcomed
      and should be addressed to{' '}
      <a href="mailto:ademola@grouth.co" className="text-[#83BF42] underline">ademola@grouth.co</a> or call{' '}
      <a href="tel:+2348117398259" className="text-[#83BF42] underline">+234 (0) 8117398259</a>.
    </p>
  </LegalPage>
);

export default Privacy;
